import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageBlock } from "@rakazo/contracts";
import { appConnectedInput, waitForConnection } from "@rakazo/core";
import {
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@rakazo/ui-web";
import { Download, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { BuiCard, SuccessPop } from "../../components/ai/primitives";
import type { ArtifactTarget } from "../../lib/artifact-open";
import { chartViewport } from "../../lib/chart-viewport";
import { connectMcpOauth, takeCompletedMcpOauth } from "../../lib/mcp-connect";
import { importChunk } from "../../lib/preload-recovery";
import { rpc } from "../../lib/rpc";
import { useArtifactImageState } from "../../lib/use-artifact-image";
import { errorText } from "../../lib/user-error";

export function ChoiceCard({
  botId,
  block,
  onBotChanged,
}: {
  botId: string;
  block: Extract<MessageBlock, { kind: "choice" }>;
  onBotChanged: () => Promise<void>;
}) {
  const { t } = useLingui();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localAnswerId, setLocalAnswerId] = useState<string | null>(null);
  const answerId = block.answerId ?? localAnswerId;
  const [locallyDismissed, setLocallyDismissed] = useState(false);
  const actionInFlight = useRef(false);
  const dismissed = locallyDismissed || answerId === "_dismissed";

  async function choose(optionId: string) {
    if (actionInFlight.current || answerId || dismissed) return;
    actionInFlight.current = true;
    setPending(true);
    setError(null);
    try {
      await rpc.onboarding.choose({ botId, optionId });
      // The choice is committed even if refreshing the roster fails.
      setLocalAnswerId(optionId);
      setPending(false);
      void Promise.resolve()
        .then(onBotChanged)
        .catch(() => undefined);
    } catch (err) {
      setError(errorText(err, t`Could not save this choice`));
      actionInFlight.current = false;
      setPending(false);
    }
  }

  async function dismiss() {
    if (actionInFlight.current || answerId || dismissed) return;
    actionInFlight.current = true;
    setPending(true);
    setError(null);
    try {
      await rpc.onboarding.dismissFocus({ botId });
      setLocallyDismissed(true);
      void Promise.resolve()
        .then(onBotChanged)
        .catch(() => undefined);
    } catch (err) {
      setError(errorText(err, t`Could not dismiss`));
      actionInFlight.current = false;
      setPending(false);
    }
  }

  if (dismissed) return null;

  return (
    <div aria-busy={pending} className="flex justify-start">
      <div className="relative w-[420px] max-w-full rounded-[20px] bg-card px-[18px] py-[14px]">
        {!answerId ? (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t`Dismiss`}
            disabled={pending}
            onClick={() => void dismiss()}
            className="absolute end-1 top-1 size-11 text-muted-foreground"
          >
            <X size={16} strokeWidth={1.8} />
          </Button>
        ) : null}
        <div className="pe-8 text-base text-foreground/90">
          <OnboardingFocusQuestion question={block.question} />
        </div>
        {block.subtitle ? (
          <div className="mt-0.5 text-base text-foreground/75">{block.subtitle}</div>
        ) : null}
        <div className="mt-3 space-y-2">
          {block.options
            .filter((option) => !answerId || option.id === answerId)
            .map((option) => (
              <button
                key={option.id}
                type="button"
                disabled={Boolean(answerId) || pending}
                onClick={() => void choose(option.id)}
                className={`flex min-h-11 w-full items-center gap-3 rounded-xl px-3.5 py-3 text-start text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:opacity-60 ${answerId ? "bg-foreground/10" : "bg-background enabled:hover:bg-foreground/5 enabled:active:bg-foreground/15"}`}
              >
                <span className="grid h-[24px] w-[24px] shrink-0 place-items-center rounded-[7px] bg-card text-[12.5px] leading-none font-medium text-foreground/75">
                  {option.letter}
                </span>
                <span
                  className={`flex-1 text-base leading-[1.35] ${answerId ? "text-foreground/75" : "text-foreground"}`}
                >
                  <OnboardingFocusOptionLabel
                    id={option.id}
                    label={option.label}
                    question={block.question}
                  />
                </span>
                {answerId === option.id ? <span className="text-foreground/75">✓</span> : null}
              </button>
            ))}
        </div>
        {error ? (
          <p role="alert" className="mt-2 text-base text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** First-run Chief focus card: API stores English; UI locale catalogs translate it. */
function OnboardingFocusQuestion({ question }: { question: string }) {
  if (question === "What do you want me on first?") {
    return <Trans>What do you want me on first?</Trans>;
  }
  return question;
}

function OnboardingFocusOptionLabel({
  id,
  label,
  question,
}: {
  id: string;
  label: string;
  question: string;
}) {
  if (question !== "What do you want me on first?") return label;
  switch (id) {
    case "day":
      return <Trans>Day-to-day work</Trans>;
    case "inbox":
      return <Trans>Inbox & email</Trans>;
    case "research":
      return <Trans>Research & writing</Trans>;
    case "everything":
      return <Trans>A bit of everything</Trans>;
    default:
      return label;
  }
}

export function AppConnectCard({
  botId,
  threadId,
  messageId,
  block,
}: {
  botId: string;
  threadId: string;
  messageId?: string;
  block: Extract<MessageBlock, { kind: "app_connect" }>;
}) {
  const { t } = useLingui();
  const [busy, setBusy] = useState(false);
  const [localStatus, setLocalStatus] = useState<"pending" | "connected">(block.status);
  const [error, setError] = useState<string | null>(null);
  const [authorizationUrl, setAuthorizationUrl] = useState<string | null>(null);
  const connectionAttempt = useRef<AbortController | null>(null);
  const status = block.status === "connected" ? "connected" : localStatus;
  useEffect(() => () => connectionAttempt.current?.abort(), []);

  async function authorize() {
    if (connectionAttempt.current || status === "connected") return;
    const controller = new AbortController();
    connectionAttempt.current = controller;
    setBusy(true);
    setError(null);
    try {
      const started = await rpc.connections.begin({
        connectorId: block.connectorId,
        provider: block.provider,
        displayName: block.name,
      });
      if (controller.signal.aborted) return;
      if (started.authorizationUrl) {
        let url: URL;
        try {
          url = new URL(started.authorizationUrl);
        } catch {
          throw new Error(t`Could not authorize this app`);
        }
        if (
          (url.protocol !== "https:" && url.protocol !== "http:") ||
          url.username ||
          url.password
        ) {
          throw new Error(t`Could not authorize this app`);
        }
        setAuthorizationUrl(url.href);
        window.open(
          url.href,
          "rakazo-app-connect",
          "popup,width=560,height=720,noopener,noreferrer",
        );
      }
      const result = await waitForConnection(
        started.connectionId,
        (input) => rpc.connections.complete(input, { signal: controller.signal }),
        { signal: controller.signal, attempts: 60 },
      );
      if (result.status === "aborted") return;
      if (result.status === "connected") {
        setLocalStatus("connected");
        await rpc.onboarding
          .appConnected(appConnectedInput(botId, threadId, block, messageId))
          .catch(() => undefined);
        return;
      }
      setError(t`Authorization timed out. Please try again.`);
    } catch (error) {
      if (!controller.signal.aborted) {
        setError(errorText(error, t`Could not authorize this app`));
      }
    } finally {
      if (connectionAttempt.current === controller) {
        connectionAttempt.current = null;
        setAuthorizationUrl(null);
        setBusy(false);
      }
    }
  }
  return (
    <BuiCard
      role="group"
      aria-label={t`${block.name} connection`}
      aria-busy={busy}
      className="w-full max-w-[440px] px-4 py-3.5"
    >
      <div className="flex flex-wrap items-center gap-3.5">
        {block.logo ? (
          <img
            src={block.logo}
            alt=""
            className="h-10 w-10 rounded-[10px] bg-white object-contain p-1"
          />
        ) : (
          <span className="grid h-10 w-10 place-items-center rounded-[10px] bg-muted text-base text-foreground">
            {block.name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <span className="min-w-0 flex-1 basis-40">
          <span className="block text-base font-medium text-foreground">{block.name}</span>
          <span className="block break-words text-base text-muted-foreground">
            {block.description}
          </span>
        </span>
        {status === "connected" ? (
          <div role="status">
            <SuccessPop label={t`Connected`} />
          </div>
        ) : (
          <Button
            className="min-h-11 rounded-full text-base"
            disabled={busy}
            onClick={() => void authorize()}
          >
            {busy ? t`Waiting…` : t`Connect`}
          </Button>
        )}
      </div>
      {busy && status !== "connected" && authorizationUrl ? (
        <a
          href={authorizationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex min-h-11 items-center rounded-md px-3 text-base underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Trans>Sign in</Trans>
        </a>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-base text-destructive">
          {error}
        </p>
      ) : null}
    </BuiCard>
  );
}

function ChartCanvas({
  spec,
  data,
  width,
  height,
}: {
  spec: Record<string, unknown>;
  data: unknown[];
  width: number;
  height?: number;
}) {
  const { t } = useLingui();
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<{
    title?: string;
    swatches: { label: string; color: string }[];
  }>({ swatches: [] });
  useEffect(() => {
    let cancelled = false;
    // Plot loads lazily so threads without charts never pay for the library.
    void (async () => {
      try {
        const { buildPlotParts } = await importChunk(() => import("@rakazo/core/plot"));
        if (cancelled || !ref.current) return;
        // Hover inspection by default: give the first mark a tooltip unless
        // the spec already asks for one somewhere.
        const marks = Array.isArray((spec as { marks?: unknown[] }).marks)
          ? ((spec as { marks: { options?: Record<string, unknown> }[] }).marks ?? [])
          : [];
        const hasTip = marks.some((mark) => mark.options && "tip" in mark.options);
        const liveSpec = hasTip
          ? spec
          : {
              ...spec,
              marks: marks.map((mark, index) =>
                index === 0 ? { ...mark, options: { ...(mark.options ?? {}), tip: true } } : mark,
              ),
            };
        const parts = buildPlotParts(liveSpec as never, data, document, { width, height });
        setMeta({ title: parts.title, swatches: parts.swatches });
        setError(null);
        ref.current.replaceChildren(parts.plotted);
      } catch (err) {
        if (!cancelled) setError(errorText(err, t`Could not render chart`));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spec, data, width, height, t]);
  if (error)
    return (
      <div className="text-[13px] text-destructive">
        <Trans>Chart failed to render: {error}</Trans>
      </div>
    );
  return (
    <div className="text-foreground/75">
      {meta.title ? (
        <div className="mb-1 text-base font-semibold text-foreground">{meta.title}</div>
      ) : null}
      {meta.swatches.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1">
          {meta.swatches.map((swatch) => (
            <span
              key={swatch.label}
              className="flex items-center gap-1.5 text-[12px] text-muted-foreground"
            >
              {/* Series colours are data-driven and intentionally distinct. */}
              <span
                className="h-[10px] w-[10px] rounded-[3px]"
                style={{ background: swatch.color }}
              />
              {swatch.label}
            </span>
          ))}
        </div>
      ) : null}
      <div ref={ref} className="[&_svg]:max-w-full" />
    </div>
  );
}

type McpApprovalState = "pending" | "connecting" | "connected" | "dismissed";

/** Approval card for an agent-created MCP server: the user completes browser
 * OAuth (or confirms no authorization is needed) without leaving the chat.
 * The decision persists on the block, so the card keeps showing it after the
 * thread remounts. */
export function McpApprovalCard({
  botId,
  threadId,
  block,
}: {
  botId: string | undefined;
  threadId: string | undefined;
  block: Extract<MessageBlock, { kind: "mcp_approval" }>;
}) {
  const { t } = useLingui();
  const { name, serverId, transport, endpoint, needsOAuth, status: savedStatus } = block;
  const [localStatus, setLocalStatus] = useState<McpApprovalState>("pending");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const actionInFlight = useRef(false);
  // A removal repaints the block to pending. Drop an in-session connected
  // override so the card does not keep the old decision.
  useEffect(() => {
    if (savedStatus === "connected" || savedStatus === "dismissed") return;
    setLocalStatus((current) =>
      current === "connected" || current === "dismissed" ? "pending" : current,
    );
  }, [savedStatus]);
  // Blocks saved before the status field existed have none and count as pending.
  const state =
    savedStatus === "connected" || savedStatus === "dismissed" ? savedStatus : localStatus;

  // Back from a full-tab sign-in for this server: finish the approval it started.
  useEffect(() => {
    if (botId && state === "pending" && takeCompletedMcpOauth(serverId)) void authorize(true);
  }, []);

  async function authorize(signedIn = false) {
    if (!botId || actionInFlight.current || state !== "pending") {
      if (!botId) setError(t`This server cannot be assigned without a bot.`);
      return;
    }
    actionInFlight.current = true;
    setBusy(true);
    setLocalStatus("connecting");
    setError(null);
    try {
      if (needsOAuth && !signedIn) {
        const result = await connectMcpOauth(serverId);
        if (result === "cancelled") {
          setLocalStatus("pending");
          return;
        }
      }
      await rpc.mcp.assignments.approve({ botId, serverId, threadId });
      setLocalStatus("connected");
    } catch (err) {
      setError(errorText(err, t`Could not approve this server`));
      setLocalStatus("pending");
    } finally {
      actionInFlight.current = false;
      setBusy(false);
    }
  }

  async function dismiss() {
    if (actionInFlight.current || state !== "pending") return;
    actionInFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      if (botId) {
        await rpc.mcp.assignments.dismiss({ botId, serverId, threadId });
      }
      setLocalStatus("dismissed");
    } catch (err) {
      setLocalStatus("pending");
      setError(errorText(err, t`Could not dismiss this server`));
    } finally {
      actionInFlight.current = false;
      setBusy(false);
    }
  }

  const summary = endpoint ?? `stdio · ${transport}`;
  return (
    <BuiCard
      aria-busy={busy}
      data-testid="mcp-approval-card"
      className="w-full min-w-0 max-w-xl p-4"
    >
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-muted text-xs text-foreground">
          M
        </span>
        <span className="text-base font-medium text-foreground">
          <Trans>Connect “{name}”</Trans>
        </span>
      </div>
      <details className="mt-1.5">
        <summary className="cursor-pointer text-[13px] text-muted-foreground">
          <Trans>Server details</Trans>
        </summary>
        <p className="mt-1 break-all text-[13px] text-muted-foreground">{summary}</p>
      </details>
      {state === "pending" || state === "connecting" ? (
        <>
          <p className="mt-2 text-base leading-[1.5] text-foreground/75">
            {needsOAuth
              ? t`Allow your assistant to use this service. Sign in in the window that opens.`
              : t`Allow your assistant to use this service’s tools.`}
          </p>
          {error ? (
            <p role="alert" className="mt-2 text-base text-destructive">
              {error}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              className="min-h-11 rounded-full text-base"
              disabled={state !== "pending" || busy}
              onClick={() => void authorize()}
            >
              {state === "connecting" ? t`Connecting…` : needsOAuth ? t`Connect` : t`Approve`}
            </Button>
            <Button
              variant="secondary"
              className="min-h-11 rounded-full text-base"
              disabled={state !== "pending" || busy}
              onClick={() => void dismiss()}
            >
              <Trans>Not now</Trans>
            </Button>
          </div>
        </>
      ) : null}
      {state === "connected" ? (
        <div role="status" className="mt-3">
          <SuccessPop label={t`Connected. Its tools are available from your next message.`} />
        </div>
      ) : null}
      {state === "dismissed" ? (
        <p className="mt-2 text-base text-muted-foreground">
          <Trans>Not connected. You can ask your assistant to connect it later.</Trans>
        </p>
      ) : null}
    </BuiCard>
  );
}

export function ChartBlockView({
  name,
  spec,
  data,
}: {
  name: string;
  spec: Record<string, unknown>;
  data: unknown[];
}) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));
  useEffect(() => {
    if (!expanded) return;
    setViewport({ width: window.innerWidth, height: window.innerHeight });
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [expanded]);
  const expandedViewport = chartViewport(viewport.width, viewport.height);
  return (
    <>
      <div className="group relative max-w-[74%] rounded-[20px] bg-muted p-4">
        <ChartCanvas spec={spec} data={data} width={520} />
        <Button
          variant="outline"
          size="xs"
          onClick={() => setExpanded(true)}
          className="absolute end-3 top-3 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        >
          <Trans>Expand</Trans>
        </Button>
      </div>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[92vh] w-[min(1320px,94vw)] max-w-none overflow-auto p-8 sm:max-w-none"
        >
          <div className="flex items-center justify-between">
            <DialogTitle className="text-[13px] font-normal text-muted-foreground">
              {name}
            </DialogTitle>
            <DialogClose
              aria-label={t`Close chart`}
              render={<Button variant="ghost" size="icon-sm" />}
            >
              <X />
            </DialogClose>
          </div>
          <ChartCanvas
            spec={spec}
            data={data}
            width={expandedViewport.width}
            height={expandedViewport.height}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// The picture is already in the browser as a blob, so the browser saves it without another request.
function ImageDownloadLink(props: { src: string; name: string; className?: string }) {
  const { t } = useLingui();
  return (
    <a
      href={props.src}
      download={props.name}
      aria-label={t`Download ${props.name}`}
      className={cn(
        buttonVariants({ variant: "ghost", size: "icon-sm" }),
        "bg-background/80 backdrop-blur-sm hover:bg-background",
        props.className,
      )}
    >
      <Download aria-hidden="true" />
    </a>
  );
}

export function ArtifactImage({
  target,
  artifactId,
  name,
}: {
  target: ArtifactTarget;
  artifactId: string;
  name: string;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const { src, error, retry } = useArtifactImageState(target, artifactId, visible);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    const element = container.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "320px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={container}>
      {src && !imageFailed ? (
        <div className="relative w-fit max-w-full">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="grid min-h-24 min-w-32 max-w-[240px] place-items-center overflow-hidden rounded-[20px] bg-muted"
          >
            <img
              src={src}
              alt={name}
              onError={() => setImageFailed(true)}
              className="max-h-48 w-full object-contain"
            />
          </button>
          <ImageDownloadLink src={src} name={name} className="absolute end-2 bottom-2" />
        </div>
      ) : (
        <div className="rounded-[20px] border border-border bg-muted px-4 py-3 text-[14px] text-muted-foreground">
          <span className="block break-all">{name}</span>
          {error || imageFailed ? (
            <div className="mt-2 flex items-center gap-2">
              <span role="alert">{t`Could not load image`}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setImageFailed(false);
                  retry();
                }}
              >{t`Retry`}</Button>
            </div>
          ) : (
            <span className="sr-only">{t`Loading…`}</span>
          )}
        </div>
      )}
      {src && !imageFailed ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            showCloseButton={false}
            className="w-auto max-w-none bg-transparent p-0 ring-0 sm:max-w-none"
          >
            <DialogTitle className="sr-only">{name}</DialogTitle>
            <ImageDownloadLink src={src} name={name} className="absolute end-11 top-2" />
            <DialogClose
              aria-label={t`Close image preview`}
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="absolute end-2 top-2 bg-background/80 backdrop-blur-sm hover:bg-background"
                />
              }
            >
              <X />
            </DialogClose>
            <img
              src={src}
              alt={name}
              className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain"
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
